/**
 * Radio group primitive — a `<fieldset>` of native `<input type="radio">`s
 * sharing `name`. The browser gives Up/Down/Left/Right roving selection
 * and screen-reader group semantics for free; a `<legend>` names the group
 * (required — no bare literal). Controlled (`value` + `onChange`).
 */
import type { JSX } from 'solid-js';
import { For } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-radio';
const CSS = `
.f4-radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: 0;
  border: none;
  margin: 0;
}
.f4-radio-group__legend { padding: 0; font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); color: var(--color-text); }
.f4-radio {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
}
.f4-radio__input {
  width: var(--space-4);
  height: var(--space-4);
  accent-color: var(--color-accent);
}
.f4-radio--disabled { opacity: 0.6; cursor: not-allowed; }
.f4-radio__label { color: var(--color-text); font-size: var(--font-size-sm); }
`;

export interface RadioOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface RadioProps {
  /** Shared `name` for the native radio group. */
  readonly name: string;
  /** Options rendered in order. */
  readonly options: readonly RadioOption[];
  /** Currently selected value. */
  readonly value: string;
  /** Emitted with the newly selected value. */
  readonly onChange: (value: string) => void;
  /** Group legend — the group's accessible name (required). */
  readonly legend: string;
  readonly class?: string;
}

export function Radio(props: RadioProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  return (
    <fieldset class={cx('f4-radio-group', props.class)}>
      <legend class="f4-radio-group__legend">{props.legend}</legend>
      <For each={props.options}>
        {(option) => (
          <label class={cx('f4-radio', option.disabled && 'f4-radio--disabled')}>
            <input
              type="radio"
              class="f4-radio__input f4-focus-ring"
              name={props.name}
              value={option.value}
              checked={props.value === option.value}
              disabled={option.disabled}
              onChange={() => props.onChange(option.value)}
            />
            <span class="f4-radio__label">{option.label}</span>
          </label>
        )}
      </For>
    </fieldset>
  );
}
